/**
 * DashboardPlanUsageCard
 *
 * Surfaces the Free-plan asset cap right on the dashboard so admins see the
 * gate the moment it bites. Auto-hides on Pro tenants. Shown when:
 *   - Tenant is on Free, AND
 *   - asset_limit is defined (non-Pro) and asset_count >= 70% of limit
 *
 * One-click "Upgrade to Pro" CTA routes to /settings/billing — the existing
 * Billing page handles the Stripe Checkout from there.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Box, Sparkles, AlertTriangle } from 'lucide-react';
import { billingApi } from '../../services/api';

const VISIBILITY_THRESHOLD = 0.7; // show widget once at 70% of cap

const DashboardPlanUsageCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .me()
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  if (data.is_pro) return null;
  if (!data.asset_limit) return null;

  const pct = data.asset_limit ? data.asset_count / data.asset_limit : 0;
  if (pct < VISIBILITY_THRESHOLD) return null;

  const atCap = data.asset_count >= data.asset_limit;
  const remaining = Math.max(0, data.asset_limit - data.asset_count);
  const barPct = Math.min(100, pct * 100);

  return (
    <Card
      className={`relative overflow-hidden border ${
        atCap ? 'border-[#FECACA] bg-[#FEF2F2]' : 'border-[#FDE68A] bg-[#FFFBEB]'
      }`}
      data-testid="dashboard-plan-usage-card"
    >
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
            atCap ? 'bg-[#FEF2F2] border border-[#FECACA]' : 'bg-[#FFFBEB] border border-[#FDE68A]'
          }`}>
            {atCap ? (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            ) : (
              <Box className="w-5 h-5 text-amber-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Free plan</span>
              <span
                className={`text-xs font-mono font-semibold ${atCap ? 'text-red-600' : 'text-amber-600'}`}
                data-testid="dashboard-plan-usage-count"
              >
                {data.asset_count} / {data.asset_limit} assets
              </span>
            </div>
            <p className={`text-sm mt-1 ${atCap ? 'text-red-200' : 'text-foreground'}`}>
              {atCap
                ? "You've hit the 10-asset cap. Upgrade to Pro for unlimited assets, Guardian AI, and per-asset GPS telemetry."
                : `Only ${remaining} asset slot${remaining === 1 ? '' : 's'} left on Free. Upgrade to Pro for unlimited assets and unlock Guardian AI.`}
            </p>
            <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden max-w-md">
              <div
                className={`h-full transition-all ${atCap ? 'bg-red-500' : 'bg-amber-400'}`}
                style={{ width: `${barPct}%` }}
              />
            </div>
          </div>
        </div>
        <Button
          onClick={() => navigate('/settings/billing')}
          className="btn-industrial shrink-0"
          data-testid="dashboard-plan-usage-upgrade-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Upgrade to Pro
        </Button>
      </CardContent>
    </Card>
  );
};

export default DashboardPlanUsageCard;
